import { Configuration } from '../generated';
import { AuthApiService } from '../../../features/auth/api/AuthApiService';

interface ErrorResponse {
    status: false;
    status_code: number;
    error_message: string;
    data: null;
}

export class ApiClient extends Configuration {

    private readonly TIMEOUT_MS = 30000;
    private authApi: AuthApiService;

    constructor(useAuth: boolean = true) {
        super();
        this.authApi = AuthApiService.getInstance();
    }

    get basePath() {
        return 'https://api-test.rxiq.clinic';
    }

    private createErrorResponse(status: number, message: string): Response {
        const errorBody: ErrorResponse = {
            status: false,
            status_code: status,
            error_message: message,
            data: null
        };
        return new Response(JSON.stringify(errorBody));
    }

    logout = () => {
        this.authApi.clearTokens();
        window.location.href = '/login';
    }

    get fetchApi() {
        return async (url: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
            const authToken = this.authApi.getAccessToken();
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            };
            
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            // Merge with existing headers
            const mergedHeaders = {
                ...headers,
                ...init?.headers as Record<string, string>
            };
            
            const requestInit: RequestInit = {
                ...init,
                signal: AbortSignal.timeout(this.TIMEOUT_MS),
                headers: mergedHeaders
            };


            try {
                let response = await fetch(url, requestInit);
                
                // Handle unauthorized (401) and invalid token (444) responses
                if ((response.status === 401 || response.status === 444) && authToken) {
                    // Try to refresh the token
                    const refreshResult = await this.authApi.refreshAccessToken(authToken);
                    if (refreshResult.isSuccess) {
                        // Retry the request with new token
                        const newAuthToken = this.authApi.getAccessToken();
                        if (newAuthToken) {
                            headers['Authorization'] = `Bearer ${newAuthToken}`;
                            requestInit.headers = { ...headers, ...init?.headers as Record<string, string> };
                            response = await fetch(url, requestInit);
                        }
                    } else {
                        // Refresh failed, redirect to login
                        this.logout();
                        return this.createErrorResponse(401, 'Session expired. Please login again.');
                    }
                }
                
                if (!response.ok) {
                    const body = await response.json();
                    return this.createErrorResponse(response.status, body.error_message || body.detail);
                }

                return response;
            } catch (error) {
                if (error instanceof DOMException && error.name === 'TimeoutError') {
                    return this.createErrorResponse(408, 'Request timeout');
                }
                return this.createErrorResponse(
                    600,
                    error instanceof Error ? error.message : 'Unknown error'
                );
            }
        };
    }

    refreshAccessToken = async (): Promise<boolean> => {
        const result = await this.authApi.refreshAccessToken();
        return result.isSuccess;
    };

}
